import { useState } from "react";
import { Menu, X, Phone, Mail, Instagram } from "lucide-react";
import { useTranslation } from "react-i18next";
import { scrollToId } from "@/lib/scroll";
import emblem from "@/assets/golden-wine-emblem.png";
import LanguageSwitcher from "./LanguageSwitcher";

const LINK_IDS = ["especialidades", "catalogo", "comparativa", "bodegas", "taller", "faq"];

export default function Navbar() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const LINKS = LINK_IDS.map((id) => ({ id, href: `#${id}`, label: t(`navbar.links.${id}`) }));

  const go = (href) => {
    setOpen(false);
    scrollToId(href);
  };

  return (
    <header data-testid="navbar" className="sticky top-0 z-50 backdrop-blur-2xl bg-[#05070A]/95 border-b border-[#F5CB5C]/15 transition-all duration-300">
      <div className="w-full max-w-7xl 2xl:max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 h-[72px] flex items-center justify-between gap-3 sm:gap-4">
        <button
          data-testid="navbar-logo"
          onClick={() => go("#inicio")}
          className="flex items-center gap-2.5 sm:gap-3 text-left shrink-0 group focus:outline-none"
        >
          <img
            src={emblem}
            alt="Láser Vintage"
            className="w-9 h-9 sm:w-10 sm:h-10 xl:w-11 xl:h-11 object-contain drop-shadow-[0_0_14px_rgba(245,203,92,0.35)] shrink-0 group-hover:scale-105 transition-transform duration-300"
          />
          <span className="shrink-0">
            <span className="block font-display text-white text-[13px] sm:text-base xl:text-lg tracking-[0.14em] leading-none whitespace-nowrap">
              LÁSER VINTAGE
            </span>
            <span className="block text-[7.5px] sm:text-[8.5px] xl:text-[9px] tracking-[0.26em] sm:tracking-[0.3em] text-[#F5CB5C]/85 mt-1 whitespace-nowrap font-medium">
              {t("navbar.brandSub")}
            </span>
          </span>
        </button>

        <nav className="hidden xl:flex items-center gap-3.5 2xl:gap-6 shrink-0">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => go(l.href)}
              className="text-xs 2xl:text-sm text-gray-300 hover:text-[#F5CB5C] transition-colors duration-200 whitespace-nowrap font-medium py-1 px-1 rounded hover:bg-white/[0.02]"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-2 sm:gap-2.5 xl:gap-3 shrink-0">
          <a
            data-testid="navbar-email"
            href="mailto:info@laservintage.es"
            className="sr-only"
            tabIndex={-1}
            aria-hidden="true"
          >
            info@laservintage.es
          </a>

          <a
            data-testid="navbar-phone"
            href="tel:+34652463252"
            title="652 463 252"
            className="hidden md:inline-flex items-center gap-1.5 rounded-full border border-[#F5CB5C]/25 px-2.5 2xl:px-3.5 py-1.5 text-xs font-semibold text-[#F5CB5C] whitespace-nowrap hover:border-[#F5CB5C]/60 hover:bg-[#F5CB5C]/10 hover:shadow-[0_0_12px_rgba(245,203,92,0.25)] transition-all duration-300"
          >
            <Phone size={13} />
            <span className="hidden 2xl:inline tabular-nums tracking-wide font-mono">652 463 252</span>
          </a>

          <a
            data-testid="navbar-instagram"
            href="https://www.instagram.com/laser__vintage"
            target="_blank"
            rel="noopener noreferrer"
            title="Instagram @laser__vintage"
            aria-label="Instagram @laser__vintage"
            className="hidden 2xl:inline-flex items-center justify-center w-8 h-8 rounded-full border border-[#F5CB5C]/25 text-[#F5CB5C] hover:border-[#F5CB5C]/60 hover:bg-[#F5CB5C]/10 hover:shadow-[0_0_12px_rgba(245,203,92,0.3)] transition-all duration-300"
          >
            <Instagram size={14} />
          </a>

          <LanguageSwitcher className="hidden sm:flex" />

          <button
            data-testid="navbar-quote-button"
            onClick={() => go("#presupuesto")}
            className="hidden sm:inline-flex items-center justify-center rounded-full px-3.5 sm:px-4 xl:px-5 py-2 sm:py-2.5 text-xs xl:text-sm font-bold text-[#05070A] bg-gradient-to-br from-[#FAE19C] via-[#F5CB5C] to-[#C69222] hover:shadow-[0_0_24px_rgba(245,203,92,0.5)] hover:-translate-y-0.5 active:translate-y-0 transition-all duration-300 whitespace-nowrap shadow-md cursor-pointer"
          >
            {t("navbar.quoteButton")}
          </button>

          <button
            data-testid="navbar-menu-button"
            onClick={() => setOpen(!open)}
            className="xl:hidden text-gray-200 p-2 focus:outline-none hover:text-[#F5CB5C] transition-colors rounded-lg"
            aria-label={t("navbar.menuAria")}
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="navbar-mobile-menu" className="xl:hidden border-t border-[#F5CB5C]/15 bg-[#0B0E14]/98 backdrop-blur-2xl transition-all duration-300 shadow-2xl">
          <div className="px-5 sm:px-6 py-5 flex flex-col gap-2">
            {LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={`nav-mobile-link-${l.id}`}
                onClick={() => go(l.href)}
                className="text-left py-3 text-sm font-medium text-gray-200 hover:text-[#F5CB5C] border-b border-white/5 last:border-0 transition-colors"
              >
                {l.label}
              </button>
            ))}

            <div className="py-3 flex items-center justify-between border-b border-white/5">
              <span className="text-xs text-gray-400 uppercase tracking-wider font-semibold">{t("navbar.langLabel")}</span>
              <LanguageSwitcher />
            </div>

            <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2">
              <a
                data-testid="navbar-mobile-email"
                href="mailto:info@laservintage.es"
                className="flex items-center justify-center gap-2 rounded-full border border-[#F5CB5C]/30 px-3 py-2.5 text-xs font-semibold text-[#F5CB5C] hover:border-[#F5CB5C]/60 hover:bg-[#F5CB5C]/5 transition-colors"
              >
                <Mail size={14} /> info@laservintage.es
              </a>
              <a
                data-testid="navbar-mobile-phone"
                href="tel:+34652463252"
                className="flex items-center justify-center gap-2 rounded-full border border-[#F5CB5C]/30 px-3 py-2.5 text-xs font-semibold text-[#F5CB5C] hover:border-[#F5CB5C]/60 hover:bg-[#F5CB5C]/5 transition-colors"
              >
                <Phone size={14} /> 652 463 252
              </a>
              <a
                data-testid="navbar-mobile-instagram"
                href="https://www.instagram.com/laser__vintage"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 rounded-full border border-[#F5CB5C]/30 px-3 py-2.5 text-xs font-semibold text-[#F5CB5C] hover:border-[#F5CB5C]/60 hover:bg-[#F5CB5C]/5 transition-colors"
              >
                <Instagram size={14} /> @laser__vintage
              </a>
            </div>

            <button
              data-testid="navbar-mobile-quote-button"
              onClick={() => go("#presupuesto")}
              className="mt-3 rounded-full px-5 py-3.5 text-sm font-bold text-[#05070A] bg-gradient-to-br from-[#FAE19C] via-[#F5CB5C] to-[#C69222] hover:shadow-[0_0_24px_rgba(245,203,92,0.45)] transition-all duration-300 text-center"
            >
              {t("navbar.quoteButton")}
            </button>
          </div>
        </div>
      )}
    </header>
  );
}

